import React from 'react';
import type { ColDef } from 'ag-grid-community';

export const signalLabel = (v: number) => v === 1 ? 'BUY' : v === 2 ? 'SELL' : 'HOLD';
export const signalColor = (v: number) => v === 1 ? '#ef4444' : v === 2 ? '#3b82f6' : '#6b7280';
export const signalBg = (v: number) => v === 1 ? '#fef2f2' : v === 2 ? '#eff6ff' : '#f9fafb';

export interface PreviewOrder {
  prodId: string;
  prodNm?: string;
  ex: string;
  wei: number;
  exitAll: boolean;
  estQty: number;
  kisQty?: number | null;
  estAmt: number;
  buyCount: number;
  sellCount: number;
  holdCount: number;
  lastClose: number;
  confidence: number;
  t0: string;
  t1: string;
  selected?: boolean;
}

export interface SimSignal {
  prodId: string;
  prodNm?: string;
  strategyName: string;
  signal: number;
  confidence: number;
  reason: string;
  lastClose: number;
  analysisDate: string;
}

export interface SimVote {
  prodId: string;
  prodNm?: string;
  decision: string;
  buyCount: number;
  sellCount: number;
  holdCount: number;
  wei: number;
}

export interface SimResult {
  fromDate: string;
  toDate: string;
  orderDate?: string;
  strategies: string[];
  signals: SimSignal[];
  votes: SimVote[];
  runId?: number;
}

export interface BaseAmtInfo {
  n2TotAmt: number;
  buyableAmt: number;
  na: number;
  bsDate: string;
}

export interface QuantStrategyInfo {
  id: number;
  code: string;
  name: string;
  type: 'daily' | 'intraday';
  description: string | null;
  defaultParams: string | null;
  enabled: boolean;
}

export const thStyle: React.CSSProperties = {
  padding: '6px 10px', textAlign: 'center', fontSize: '11px', fontWeight: 600,
  whiteSpace: 'nowrap', position: 'sticky', top: 0,
};
export const tdStyle: React.CSSProperties = {
  padding: '6px 10px', whiteSpace: 'nowrap', fontSize: '12px',
};
export const tabStyle: React.CSSProperties = {
  padding: '5px 12px', borderRadius: '3px', border: 'none',
  backgroundColor: '#3b82f6', color: 'white', cursor: 'pointer', fontSize: '12px', fontWeight: 500,
};
export const tabActiveStyle: React.CSSProperties = {
  ...tabStyle, backgroundColor: '#1e40af',
};
export const filterStyle: React.CSSProperties = {
  padding: '5px 12px', borderRadius: '3px', border: 'none',
  backgroundColor: '#e5e7eb', color: '#374151', cursor: 'pointer', fontSize: '12px',
};
export const paramBoxStyle: React.CSSProperties = {
  backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px',
  padding: '10px', display: 'flex', flexDirection: 'column', gap: '6px',
};
export const paramTitleStyle: React.CSSProperties = {
  fontSize: '11px', fontWeight: 700, color: '#1e40af', marginBottom: '2px',
};

export const previewBaseColDefs: ColDef<PreviewOrder>[] = [
  { headerName: '종목코드', field: 'prodId', flex: 1, minWidth: 75 },
  { headerName: '종목명', field: 'prodNm', flex: 2, minWidth: 100 },
  {
    headerName: 'LS/EX', field: 'ex', flex: 0.8, minWidth: 55,
    valueFormatter: (p) => p.value === 'E' ? 'L / E' : 'L / X',
    cellStyle: (p) => ({ fontWeight: 700, color: p.value === 'E' ? '#ef4444' : '#3b82f6', textAlign: 'center' }),
  },
  {
    headerName: '비중(%)', field: 'wei', flex: 0.8, minWidth: 55, type: 'numericColumn',
    valueFormatter: (p) => p.value > 0 ? p.value.toString() : '-',
  },
  {
    headerName: 'KIS가능', field: 'kisQty', flex: 0.9, minWidth: 65, type: 'numericColumn',
    valueFormatter: (p) => p.value != null ? p.value.toLocaleString() : '-',
    cellStyle: { color: '#6b7280' },
  },
  {
    headerName: '예상수량', field: 'estQty', flex: 1, minWidth: 70, type: 'numericColumn',
    valueFormatter: (p) => p.value > 0 ? p.value.toLocaleString() : '-',
    cellStyle: { fontWeight: 700 },
  },
  {
    headerName: '예상금액', field: 'estAmt', flex: 1.2, minWidth: 85, type: 'numericColumn',
    valueFormatter: (p) => p.value > 0 ? p.value.toLocaleString() : '-',
  },
  {
    headerName: '종가', field: 'lastClose', flex: 1.2, minWidth: 80, type: 'numericColumn',
    valueFormatter: (p) => p.value?.toLocaleString() ?? '',
  },
  {
    headerName: '신뢰도(%)', field: 'confidence', flex: 0.8, minWidth: 60, type: 'numericColumn',
  },
  { headerName: '시작', field: 't0', flex: 0.7, minWidth: 48, cellStyle: { textAlign: 'center' } },
  { headerName: '종료', field: 't1', flex: 0.7, minWidth: 48, cellStyle: { textAlign: 'center' } },
];
